<script setup lang="ts">
import Drawer from 'primevue/drawer';
import { UiButton } from '@peanuds/ui';
import { useTranslation } from 'i18next-vue';

const { title, subtitle, testId } = defineProps<{
  title: string;
  subtitle?: string;
  testId: string;
}>();

const visible = defineModel<boolean>('visible', { required: true });

const { t } = useTranslation();
</script>

<template>
  <Drawer
    v-model:visible="visible"
    class="card-drawer"
    position="right"
    :style="{ width: 'var(--drawer-width-lg)', maxWidth: '100vw' }"
    :pt="{ mask: { style: { background: 'transparent !important' } } }"
  >
    <template #container>
      <div class="card-drawer__container" :data-testid="testId">
        <header class="card-drawer__header">
          <div class="card-drawer__heading">
            <h3 class="card-drawer__title">{{ title }}</h3>
            <p v-if="subtitle" class="card-drawer__subtitle">{{ subtitle }}</p>
          </div>
          <UiButton
            severity="secondary"
            variant="ghost"
            size="s"
            :aria-label="t('cards.dialogs.close')"
            data-testid="card-drawer-close"
            @click="visible = false"
          >
            <template #icon>
              <i class="pi pi-times" />
            </template>
          </UiButton>
        </header>
        <div class="card-drawer__body">
          <slot />
        </div>
        <footer v-if="$slots.footer" class="card-drawer__footer">
          <slot name="footer" />
        </footer>
      </div>
    </template>
  </Drawer>
</template>

<style scoped lang="scss">
.card-drawer {
  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: $spacing-xl;
    background: $color-bg-widget;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: $spacing-md;
    padding-bottom: $spacing-md;
  }

  &__heading {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
  }

  &__title {
    margin: 0;
    font-size: $font-size-2xl;
    font-weight: $font-weight-medium;
    line-height: $line-height-xl;
  }

  &__subtitle {
    margin: 0;
    color: $color-text-secondary;
    font-size: $font-size-sm;
  }

  &__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-md;
    min-height: 0;
    overflow-y: auto;
  }

  &__footer {
    display: flex;
    gap: $spacing-s;
    padding-top: $spacing-md;
  }
}
</style>
